import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  buildI18nResources,
  DEFAULT_LANGUAGE_CODE,
  languageCodeToDir,
  normalizeLanguageCode,
  type LanguageCode,
} from '@/i18n/resources';
import { lazyI18nBackend } from '@/i18n/lazyBackend';

let initialized = false;
const INITIAL_NAMESPACES = ['common', 'navigation'] as const;

export async function initI18n(languageCode?: string | null): Promise<LanguageCode> {
  const resolved = normalizeLanguageCode(languageCode);

  if (!initialized) {
    await i18n.use(lazyI18nBackend).use(initReactI18next).init({
      lng: resolved,
      fallbackLng: DEFAULT_LANGUAGE_CODE,
      defaultNS: 'common',
      ns: INITIAL_NAMESPACES,
      fallbackNS: 'common',
      resources: buildI18nResources(),
      partialBundledLanguages: true,
      interpolation: { escapeValue: false },
      returnEmptyString: false,
      load: 'currentOnly',
      cleanCode: false,
      lowerCaseLng: false,
      saveMissing: import.meta.env.DEV,
      missingKeyHandler: (languages, namespace, key, fallbackValue) => {
        if (!import.meta.env.DEV) return;
        // i18next passes defaultValue as fallbackValue; skip expected fallbacks.
        if (fallbackValue != null && fallbackValue !== key && fallbackValue !== '') return;
        console.warn('[i18n] Missing translation', { languages, namespace, key });
      },
    });
    initialized = true;
  } else {
    await i18n.changeLanguage(resolved);
  }

  applyDocumentLanguage(resolved);
  return resolved;
}

export function applyDocumentLanguage(languageCode: LanguageCode): void {
  const html = document.documentElement;
  html.lang = languageCode.replace('_', '-');
  html.dir = languageCodeToDir(languageCode);
}

export { i18n };
